import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import FormProvider, { RHFSelect } from 'Components/Organisms/HookForm';
import Button from 'Components/Atoms/Button/Button';
import useCountries from 'Hooks/useCountries';
import { INVOICE_SUBTYPES } from 'Utils/constants';
import AlertForm from 'Components/Molecules/AlertForm/AlertForm';
import Divider from 'Components/Atoms/Divider/Divider';

const getInvoiceDocTypes = (t) => [
    { value: null, label: t('tax-authority-config-all-option') },
    { value: 'R1', label: t('ticketbai-invoice-type-r1') },
    { value: 'R2', label: t('ticketbai-invoice-type-r2') },
    { value: 'R3', label: t('ticketbai-invoice-type-r3') },
    { value: 'R4', label: t('ticketbai-invoice-type-r4') },
    { value: 'R5', label: t('ticketbai-invoice-type-r5') },
];

const getIssuerTypeOptions = (t) => [
    { value: null, label: t('ticketbai-issuer-type-direct') },
    { value: 'T', label: t('ticketbai-issuer-type-third-party') },
    { value: 'D', label: t('ticketbai-issuer-type-self-invoice') },
];

const getRegimeOptions = (t) => [
    { value: '01', label: t('ticketbai-regime-01') },
    { value: '02', label: t('ticketbai-regime-02') },
    { value: '03', label: t('ticketbai-regime-03') },
    { value: '04', label: t('ticketbai-regime-04') },
    { value: '05', label: t('ticketbai-regime-05') },
    { value: '06', label: t('ticketbai-regime-06') },
    { value: '07', label: t('ticketbai-regime-07') },
    { value: '08', label: t('ticketbai-regime-08') },
    { value: '09', label: t('ticketbai-regime-09') },
    { value: '10', label: t('ticketbai-regime-10') },
    { value: '11', label: t('ticketbai-regime-11') },
    { value: '12', label: t('ticketbai-regime-12') },
    { value: '13', label: t('ticketbai-regime-13') },
    { value: '14', label: t('ticketbai-regime-14') },
    { value: '15', label: t('ticketbai-regime-15') },
    { value: '17', label: t('ticketbai-regime-17') },
    { value: '19', label: t('ticketbai-regime-19') },
    { value: '51', label: t('ticketbai-regime-51') },
    { value: '52', label: t('ticketbai-regime-52') },
    { value: '53', label: t('ticketbai-regime-53') },
    { value: '54', label: t('ticketbai-regime-54') },
];

export default function InvoicesTaxAuthorityConfigTicketbaiForm({
    defaultValues,
    onSave,
    onClose,
    onSuccess,
    onError,
}) {
    const { t } = useTranslation('invoices');
    const { t: tContacts } = useTranslation('contacts');
    const { countries } = useCountries();
    const [conflictError, setConflictError] = useState(false);

    const countriesOptions = useMemo(
        () => [
            { value: null, label: t('tax-authority-config-all-option') },
            ...(countries ?? [])
                .map((c) => ({ value: c.code, label: c.name }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        ],
        [countries, t],
    );

    const invoiceSubtypeOptions = useMemo(
        () => [
            { value: null, label: t('tax-authority-config-all-option') },
            { value: INVOICE_SUBTYPES.STANDARD, label: t('invoice-standard') },
            { value: INVOICE_SUBTYPES.STANDARD_CORRECTIVE, label: t('invoice-standard-corrective') },
            { value: INVOICE_SUBTYPES.SIMPLIFIED, label: t('invoice-simplified') },
            { value: INVOICE_SUBTYPES.SIMPLIFIED_CORRECTIVE, label: t('invoice-simplified-corrective') },
        ],
        [t],
    );

    const contactTypeOptions = useMemo(
        () => [
            { value: null, label: t('tax-authority-config-all-option') },
            { value: 'company', label: tContacts('company') },
            { value: 'freelancer', label: tContacts('freelancer') },
            { value: 'individual', label: tContacts('individual') },
        ],
        [t, tContacts],
    );

    const schema = yup.object().shape({
        country: yup.string().nullable(),
        es_tbai_doc_type: yup.string().nullable(),
        es_tbai_issuer_type: yup.string().nullable(),
        es_tbai_regime: yup.string().required(t('field-required')),
    });

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues,
    });

    const {
        handleSubmit,
        formState: { isSubmitting },
    } = methods;

    const submit = async (data) => {
        try {
            await onSave({
                country: data.country,
                invoice_subtype: data.invoice_subtype,
                contact_type: data.contact_type,
                contact_id: null,
                es_tbai_doc_type: data.es_tbai_doc_type,
                es_tbai_issuer_type: data.es_tbai_issuer_type,
                es_tbai_regime: data.es_tbai_regime,
            });
            onSuccess();
        } catch (error) {
            if (error.response?.status === 409) {
                setConflictError(true);
            } else {
                onError(error);
            }
        }
    };

    return (
        <FormProvider className="flex flex-col h-full" methods={methods} onSubmit={handleSubmit(submit)}>
            <div className="grow py-6">
                <div className="grid gap-5">
                    {conflictError && (
                        <AlertForm className="mb-3" onClose={() => setConflictError(false)}>
                            {t('tax-authority-config-duplicate-error')}
                        </AlertForm>
                    )}
                    <RHFSelect
                        className="w-full"
                        name="country"
                        label={t('tax-authority-config-country-label')}
                        options={countriesOptions}
                        data-autofocus="true"
                        enableFilter
                    />
                    <RHFSelect
                        className="w-full"
                        name="invoice_subtype"
                        label={t('tax-authority-config-invoice-subtype-label')}
                        options={invoiceSubtypeOptions}
                    />
                    <RHFSelect
                        className="w-full"
                        name="contact_type"
                        label={t('tax-authority-config-contact-type-label')}
                        options={contactTypeOptions}
                    />

                    <Divider />

                    <RHFSelect
                        className="w-full"
                        name="es_tbai_doc_type"
                        label={t('ticketbai-invoice-type-label')}
                        options={getInvoiceDocTypes(t)}
                    />
                    <RHFSelect
                        className="w-full"
                        name="es_tbai_regime"
                        label={t('ticketbai-regime-label')}
                        options={getRegimeOptions(t)}
                    />
                    <RHFSelect
                        className="w-full"
                        name="es_tbai_issuer_type"
                        label={t('ticketbai-issuer-type-label')}
                        options={getIssuerTypeOptions(t)}
                    />
                </div>
            </div>
            <div className="sticky bottom-0 bg-white pt-2 pb-4 z-10">
                <div className="flex gap-2">
                    <Button variant="primary" isBusy={isSubmitting} type="submit" className="grow">
                        {t('save')}
                    </Button>
                    <Button onClick={onClose} className="grow">
                        {t('cancel')}
                    </Button>
                </div>
            </div>
        </FormProvider>
    );
}

InvoicesTaxAuthorityConfigTicketbaiForm.propTypes = {
    defaultValues: PropTypes.object.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
};
